import { Config } from "atoms/Rtool/newConfigAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import { throwIfNotOk } from "./apiError";

// URL の powertrain セグメント（/calcs/<powertrain>/...）は引数で切り替える。
// 既定は "bev" のため、BEV 既存呼び出し・テストは不変。HEV では "hev" を渡す。
// 注: HEV で calculation 配下に gear セグメントが入るエンドポイント（諸元/計算/コピー/描画）は
// 構造が異なるため、本ファイルの prefix 引数化とは別に gear 対応版を用意する（HEV対応仕様 §3）。

// 計算セット一覧取得
export const fetchProjects = async (
    authToken: string,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.project_id;
};

// 仕向け地一覧取得
export const fetchDestinations = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /destinations`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.destination_id;
};

// ロット一覧取得
export const fetchLots = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /lots`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.lot_id;
};

// フェーズ一覧取得
export const fetchPhases = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /phases`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.phase_id;
};

// ユニット一覧取得
export const fetchUnits = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /units`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.unit_id;
};

// 計算ID一覧取得
export const fetchCalculations = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /calculations`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.calculation_id;
};

// 作成済み計算セットのメタ情報取得
export type CalculationSetData = {
    description: string;
    mtg: number;
    correction_direction: string[];
};

export type CalculationSetConfig = Pick<
    Config,
    "project_id" | "destination_id" | "lot_id" | "phase_id" | "unit_id" | "calculation_id"
>;

export const fetchCalculationSet = async (
    authToken: string,
    config: CalculationSetConfig,
    powertrain: Powertrain = "bev",
): Promise<CalculationSetData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data;
};

// 諸元: gear_ratio / motor_order とも {phenomenon_id: float} のフラット形式（BEV/HEV共通）。
// 未登録時も 200 + 空 dict（gear_ratio/motor_order は {}、tire_radius は null）で返る
// （諸元データ仕様変更 #27。登録(POST)と同一URLにGETを生やす。envelope は無い）。
export type DimensionsData = {
    tire_radius: number | null;
    gear_ratio: Record<string, number>;
    motor_order: Record<string, number>;
};

export type HevDimensionsData = DimensionsData;

// 諸元データ取得（calc 単位。登録(POST)と同一URL）
export const fetchDimensions = async (
    authToken: string,
    config: CalculationSetConfig,
): Promise<DimensionsData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/dimensions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    return await response.json();
};

// グラフ描画用のデータの取得
// 注: HEV では condition の上位に gear が入る（.../<calc>/<gear>/<condition>/datasets/<graph>）。
// BEV はこの関数を継続使用し、HEV gear 対応は別途追加する。
export const fetchGraphData = async (
    authToken: string,
    config: Config,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(config.condition_id)}
    /datasets
    /${encodeURIComponent(config.graph_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data;
};

// ============================================================
// HEV 専用（calculation 配下に gear 階層が入る。HEV対応仕様 §3.2 / openapi calcs.yaml）
// ============================================================

// HEV: gear 一覧取得（当該 (calculation, unit) に登録済みの gear_id を返す。HEV 専用）
export const fetchGears = async (
    authToken: string,
    config: CalculationSetConfig,
): Promise<string[]> => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /gears`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data.gear_id;
};

// HEV: 諸元データ取得（gear 単位。登録(POST)と同一URL）
export const fetchDimensionsHev = async (
    authToken: string,
    config: CalculationSetConfig,
    gear_id: string,
): Promise<HevDimensionsData> => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /datasets/dimensions`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    return await response.json();
};

// HEV: グラフ描画用データ取得（condition の上位に gear が入る）
export const fetchGraphDataHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/calcs/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.unit_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(config.condition_id)}
    /datasets
    /${encodeURIComponent(config.graph_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchRtool");

    const body = await response.json();
    return body.data;
};
